import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import DocumentUploadArea from "../../common/DocumentUploadArea/DocumentUploadArea";
import ChatWidget from "../../common/ChatWidget/ChatWidget";
import {
  Clock,
  TriangleAlert,
  Banknote,
  Clipboard,
  RotateCcw,
  ShieldUser,
  File,
} from "lucide-react";
import { estadosDisponibles } from "@/config/constants";
import { useNotifications } from "@/hooks/useNotifications";
import TramiteFormModal from "@/components/common/TramiteFormModal/TramiteFormModal";
import TimelineStep from "@/components/common/TimelineStep/TimelineStep";
import CotizacionModal from "@/components/common/CotizacionModal/CotizacionModal";
import StatusChangeModal from "@/components/common/StatusChangeModal/StatusChangeModal";
import GestorAssignmentModal from "@/components/common/GestorAssignmentModal/GestorAssignmentModal";

const getStatusInfo = (status) => {
  return (
    estadosDisponibles.find((e) => e.id === status) || {
      icon: "❓",
      color: "gray",
      name: status,
    }
  );
};

const RequestPanel = () => {
  const { success, error } = useNotifications();
  const { solicitudId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [solicitud, setSolicitud] = useState(null);
  const [tramiteInfo, setTramiteInfo] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [gestorInfo, setGestorInfo] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [gestoresDisponibles, setGestoresDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGestorModal, setShowGestorModal] = useState(false);
  const [tramites, setTramites] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [cotizacion, setCotizacion] = useState({});

  useEffect(() => {
    if (solicitudId) {
      fetchSolicitudCompleta();
    }
  }, [solicitudId]);

  const fetchSolicitudCompleta = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Para admins, obtener gestores disponibles
      if (user?.role === "admin") {
        const gestoresRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/managers/available-for-assignment`,
          { headers },
        );
        if (gestoresRes.ok) {
          const gestoresData = await gestoresRes.json();
          setGestoresDisponibles(gestoresData.gestores || []);
        }
      }

      // Usar endpoint unificado optimizado
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/applications/${solicitudId}/complete`,
        { headers },
      );

      if (response.ok) {
        const data = await response.json();

        setSolicitud(data.solicitud);
        setTramiteInfo(data.tramite);
        setClienteInfo(data.cliente);
        setGestorInfo(data.gestor);
        setDocuments(data.documentos);
        setTramites(data.form);

        if (data.cotizacion) {
          setCotizacion(data.cotizacion);
        }
      } else {
        console.error("❌ Error HTTP:", response.status);
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 401) {
          success("❌ Sesión expirada. Inicia sesión nuevamente.");
          navigate("/login");
        } else if (response.status === 403) {
          success(
            `❌ ${errorData.detail || "No tienes acceso a esta solicitud"}`,
          );
          navigate("/applications");
        } else if (response.status === 404) {
          success("❌ Solicitud no encontrada. Puede haber sido eliminada.");
          navigate("/applications");
        } else {
          success("❌ Error del servidor. Intenta de nuevo más tarde.");
          navigate("/applications");
        }
      }
    } catch (error) {
      console.error("❌ Error de red:", error);
      success("❌ Error de conexión. Verifica tu internet e intenta de nuevo.");
      navigate("/applications");
    } finally {
      setLoading(false);
    }
  };

  const handleAsignarGestor = async (gestorId, motivo = "") => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/applications/${solicitudId}/assign-manager?new_gestor_id=${gestorId}&assignment_reason=${encodeURIComponent(motivo)}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const result = await response.json();
        fetchSolicitudCompleta(); // Refresh data
        setShowGestorModal(false);
      } else {
        const error = await response.json();
        success(`❌ Error: ${error.detail}`);
      }
    } catch (error) {
      success("❌ Error asignando gestor");
    }
  };

  if (loading) {
    return (
      <LoadingSpinner message="Cargando panel unificado de solicitud..." />
    );
  }

  if (!solicitud || !tramiteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Solicitud no encontrada
          </h2>
          <p className="text-gray-600 mb-6">
            La solicitud no existe o no tienes acceso.
          </p>
          <button
            onClick={() => navigate("/applications")}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← Volver a Solicitudes
          </button>
        </div>
      </div>
    );
  }

  const estadoInfo = getStatusInfo(solicitud.status);

  const isEditing =
    user.role === "admin" || user.role === "gestor" || solicitud.form != null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/applications")}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Volver a Lista
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex row items-center">
                  {tramiteInfo.name}
                </h1>
                <p className="text-gray-600">
                  ID: {solicitudId.substring(0, 8)}... • Cliente:{" "}
                  {user?.role === "cliente"
                    ? "Tú"
                    : clienteInfo?.name || "Cliente"}{" "}
                  • Categoría: {tramiteInfo.categoria_nombre}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span
                className={`px-4 py-2 rounded-full text-sm font-medium gap-2 flex items-center bg-${estadoInfo.color}-100 text-${estadoInfo.color}-800`}
              >
                {estadoInfo.icon} {estadoInfo.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout Principal 2 Columnas */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header con progreso */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Documentos para {tramiteInfo.nombre}
            </h2>
            <div className="text-right">
              <div className="text-sm text-gray-500">Formularios</div>
              <div className="font-bold text-lg text-green-600">
                {solicitud.form.completed ? tramites.length : 0}/
                {tramites.length} Completados
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Documentos</div>
              <div className="font-bold text-lg text-green-600">
                {documents.aprobados}/{documents.total} Aprobados
              </div>
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div
              className="bg-green-600 h-4 rounded-full transition-all duration-500"
              style={{
                width: `${
                  documents.total > 0
                    ? (documents.aprobados / documents.total) * 100
                    : 0
                }%`,
              }}
            ></div>
          </div>

          <p className="text-sm text-gray-600">
            {documents.all_approved
              ? "🎉 ¡Todos los documentos han sido aprobados! Tu trámite está listo para cotización."
              : `Subidos: ${documents.pendientes}/${documents.total} • ` +
                `Aprobados: ${documents.aprobados}/${documents.total}`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:h-full md:h-screen">
          {/* COLUMNA IZQUIERDA: INFORMACIÓN + GESTIÓN */}
          <div className="space-y-6 lg:overflow-y-auto col-span-2">
            {/* Panel de Información */}
            <SolicitudInfoPanel
              solicitud={solicitud}
              tramiteInfo={tramiteInfo}
              estadoInfo={estadoInfo}
              gestorInfo={gestorInfo}
              user={user}
              gestoresDisponibles={gestoresDisponibles}
              onAsignarGestor={handleAsignarGestor}
              onShowGestorModal={() => setShowGestorModal(true)}
              cotizacion={cotizacion.total ?? 0}
            />

            {/* Panel de Gestión (Solo gestor/admin) */}
            {(user?.role === "gestor" || user?.role === "admin") && (
              <SolicitudManagementPanel
                solicitud={solicitud}
                cotizacion={cotizacion}
                onStatusChange={() => fetchSolicitudCompleta()}
              />
            )}
          </div>

          {/* COLUMNA DERECHA: DOCUMENTOS Y FORMULARIO*/}
          <div className="rounded-2xl overflow-y-auto pb-10 col-span-3 sm:h-[100%] pb-3">
            <div className="space-y-4 bg-white p-8 pt-5 rounded-2xl">
              <h1>Formularios</h1>

              {tramites.length > 0 &&
                tramites.map(
                  (t) =>
                    t && (
                      <div
                        key={t.id}
                        className="p-6 bg-white shadow rounded-xl flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <h2 className="text-lg font-bold flex row items-center gap-2">
                            <Clipboard /> {t.name}
                          </h2>
                          <p className="text-gray-600 text-sm">
                            {t.description}
                          </p>
                          <p className="text-gray-600 text-sm">
                            Estado:{" "}
                            {solicitud.form
                              ? solicitud.form.completed
                                ? "Completo"
                                : "Incompleto"
                              : "Incompleto"}
                          </p>
                        </div>

                        <button
                          onClick={() => setSelectedForm(t)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                        >
                          {isEditing
                            ? "Editar Formulario"
                            : "Completar Formulario"}
                        </button>
                      </div>
                    ),
                )}

              {selectedForm && (
                <TramiteFormModal
                  form={selectedForm}
                  application={solicitud}
                  onClose={() => setSelectedForm(null)}
                  edit={isEditing}
                />
              )}
            </div>

            {/* Panel de Documentos */}
            <DocumentUploadArea
              tramiteRequestId={solicitudId}
              onDocumentsChange={(docs) => console.log("Docs changed:", docs)}
              onAllApproved={() => console.log("All docs approved")}
            />
          </div>

          <div
            style={{ position: "fixed", right: 5, bottom: 20, maxWidth: 400, zIndex: 999 }}
          >
            <ChatWidget
              tramiteRequestId={solicitudId}
              isVisible={true}
              onToggleVisibility={() => {}}
              tramiteName={tramiteInfo.name}
            />
          </div>
        </div>
      </div>

      {/* Modal Asignación Gestor */}
      {showGestorModal && (
        <GestorAssignmentModal
          gestoresDisponibles={gestoresDisponibles}
          onAssign={handleAsignarGestor}
          onClose={() => setShowGestorModal(false)}
        />
      )}
    </div>
  );
};

const SolicitudInfoPanel = ({
  solicitud,
  tramiteInfo,
  gestorInfo,
  user,
  onShowGestorModal,
  cotizacion,
}) => (
  <div className="">
    {/* Información del trámite */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2 flex row items-center">
          <Banknote className="mr-1" />
          Información de Precio
        </h3>
        <p className="text-blue-800">
          {cotizacion > 0 ? `$${cotizacion} MXN` : "Aún no cotizado"}
        </p>
      </div>

      <div className="bg-green-50 p-4 rounded-lg">
        <h3 className="font-medium text-green-900 mb-2 flex row items-center">
          <Clock className="mr-1" /> Tiempo Estimado
        </h3>
        <p className="text-green-800">
          {tramiteInfo.estimated_time_days} días hábiles
        </p>
      </div>
    </div>

    {/* Asignación de Gestor */}
    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-yellow-900 mb-1 flex row items-center">
            <ShieldUser />
            Gestor Responsable
          </h3>
          <p className="text-yellow-800">
            {gestorInfo ? (
              <span className="font-medium">{gestorInfo.name}</span>
            ) : (
              <span className="text-orange-600 font-medium flex row items-center">
                <TriangleAlert className="mr-1" /> Sin asignar
              </span>
            )}
          </p>
        </div>

        {user?.role === "admin" && (
          <button
            onClick={onShowGestorModal}
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium flex row items-center"
          >
            <RotateCcw className="mr-1" /> {gestorInfo ? "Cambiar" : "Asignar"}{" "}
            Gestor
          </button>
        )}
      </div>
    </div>

    {/* Timeline de estados */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="font-medium text-gray-900 mb-3">Timeline del proceso</h3>
      <div className="space-y-2">
        <TimelineStep
          estado="cotizacion_solicitada"
          actual={solicitud.status}
        />
        <TimelineStep
          estado="documentos_en_revision"
          actual={solicitud.status}
        />
        <TimelineStep estado="en_proceso" actual={solicitud.status} />
        <TimelineStep estado="completado" actual={solicitud.status} />
      </div>
    </div>
  </div>
);

const SolicitudManagementPanel = ({ solicitud, onStatusChange, cotizacion }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCotizacionModal, setShowCotizacionModal] = useState(false);
  const { success, error } = useNotifications();

  const handleCambiarEstado = () => {
    setShowStatusModal(true);
  };

  const handleCrearCotizacion = () => {
    setShowCotizacionModal(true);
  };

  const handleViewHistory = () => {
    // Por ahora mostrar alerta, después implementar modal de historial
    success(
      `📄 Historial de la solicitud ${solicitud.id.substring(0, 8)}... - Por implementar`,
    );
  };

  return (
    <div className="bg-white p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">
        Gestión de Solicitud
      </h2>

      <div className="space-y-4">
        <button
          onClick={handleCambiarEstado}
          className="w-full bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors text-left"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium flex row items-center">
              <RotateCcw className="mr-1" />
              Cambiar Estado
            </span>
            <span>→</span>
          </div>
        </button>

        <button
          onClick={handleCrearCotizacion}
          className="w-full bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors text-left"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium flex row items-center">
              <Banknote className="mr-1" /> Editar Cotización
            </span>
            <span>→</span>
          </div>
        </button>

        <button
          onClick={handleViewHistory}
          className="w-full bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors text-left"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium flex row items-center">
              <File className="mr-1" /> Historial Completo
            </span>
            <span>→</span>
          </div>
        </button>
      </div>

      {/* Modal para cambiar estado */}
      {showStatusModal && (
        <StatusChangeModal
          currentStatus={solicitud.status}
          solicitudId={solicitud.id}
          onClose={() => setShowStatusModal(false)}
          onStatusChanged={() => {
            setShowStatusModal(false);
            onStatusChange();
          }}
        />
      )}

      {/* Modal para cotización */}
      {showCotizacionModal && (
        <CotizacionModal
          solicitudId={solicitud.id}
          cotizacion={cotizacion}
          onClose={() => setShowCotizacionModal(false)}
          onCotizacionCreated={() => {
            setShowCotizacionModal(false);
            onStatusChange();
          }}
        />
      )}
    </div>
  );
};

export default RequestPanel;
